import { NgTemplateOutlet } from '@angular/common';
import {
  injectFloatingLayout,
  injectFloatingPageClasses,
  provideSystemTabsLayout,
} from '../../../../../common/helpers/system-tabs-layout/system-tabs-layout';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
  OnInit,
  OnDestroy,
} from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { TranslocoPipe } from '@jsverse/transloco';
import {
  SearchIconComponent,
  ChevronDownIconComponent,
  EditIconComponent,
} from '@kuiper/icons';
import { JobTitleService } from '../job-title.service';
import { jobTitleDetails } from '../job-title-model';
import { toObservable } from '@angular/core/rxjs-interop';
import { debounceTime, skip, Subscription } from 'rxjs';
import { FormsModule } from '@angular/forms';
import { PaginatorComponent } from '../../../../../../ui-components/lib/paginator/paginator.component';
import { UserStatusPipe } from '../../../../../common/pipes/user-status/user-status.pipe';
import { TenantPathPipe } from '../../../../../common/pipes/tenant-path/tenant-path.pipe';
import { BadgeComponent } from '../../../../../../ui-components/lib/badge/badge.component';
import { GroupUserCardComponent } from '../../../../../common/components/group-user-card/group-user-card.component';
import { AvatarComponent } from '../../../../../../ui-components/lib/avatar/avatar.component';
import { OverlayLoaderComponent } from '../../../../../common/components/overlay-loader/overlay-loader.component';
import { ADMIN_ROUTES } from '../../../../../common/constants/admin.routing.constants';
import { TenantService } from '../../../../../common/services/tenant/tenant.service';
import { Option } from '../../../../../common/models/option.model';
import { SearchMinLength } from '../../../../../common/enums/search-min-length';

@Component({
  selector: 'lib-job-title-details',
  imports: [
    NgTemplateOutlet,
    RouterLink,
    TranslocoPipe,
    AvatarComponent,
    GroupUserCardComponent,
    BadgeComponent,
    OverlayLoaderComponent,
    PaginatorComponent,
    FormsModule,
    SearchIconComponent,
    ChevronDownIconComponent,
    EditIconComponent,
    TenantPathPipe,
    UserStatusPipe,
  ],
  providers: [provideSystemTabsLayout()],
  templateUrl: './job-title-details.component.html',
  styleUrl: './job-title-details.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class JobTitleDetailsComponent implements OnInit, OnDestroy {
  readonly ADMIN_ROUTES = ADMIN_ROUTES;
  protected readonly floating = injectFloatingLayout();
  protected readonly floatingPageClasses = injectFloatingPageClasses();

  route = inject(ActivatedRoute);
  jobTitleService = inject(JobTitleService);
  tenantService = inject(TenantService);

  subscriptions = new Subscription();
  totalUsersCount = signal<string>('');
  jobTitleDetails = signal<jobTitleDetails | null>(null);
  isLoading = signal<boolean>(false);
  jobTitleId = '';
  currentPage = signal<number>(1);
  currentSize = signal<number>(15);
  totalPages = signal<number>(20);
  jobTitleUsers = signal<Option[]>([]);
  searchTerm = signal<string>('');
  private readonly searchTerm$ = toObservable(this.searchTerm);
  isMoreThanThreeCharacters = signal<boolean>(false);
  minSearchLength = signal<number>(SearchMinLength.length);

  constructor() {
    this.subscriptions.add(
      this.route.params.subscribe(
        (params: any) => (this.jobTitleId = params.id)
      )
    );
    this.getJobTitleDetails();
    this.getJobTitleUsers();
  }

  ngOnInit() {
    this.subscriptions.add(
      this.searchTerm$.pipe(skip(1), debounceTime(600)).subscribe(term => {
        this.handleSearchTerm(term);
      })
    );
  }

  getJobTitleDetails() {
    this.isLoading.set(true);
    this.subscriptions.add(
      this.jobTitleService.getJobTitleDetails(this.jobTitleId).subscribe({
        next: res => {
          this.jobTitleDetails.set(res.data);
        },
        error: () => {
          this.isLoading.set(false);
        },
        complete: () => {
          this.isLoading.set(false);
          this.getJobTitleUsers();
        },
      })
    );
  }

  handleSearchTerm(term: string): void {
    if (term.length >= this.minSearchLength()) {
      this.isMoreThanThreeCharacters.set(true);
      this.getJobTitleUsers(term);
    } else if (
      this.isMoreThanThreeCharacters() &&
      term.length < this.minSearchLength()
    ) {
      this.isMoreThanThreeCharacters.set(false);
      this.getJobTitleUsers();
    }
  }

  onSearchTermChange(term: string) {
    this.searchTerm.set(term);
  }

  getJobTitleUsers(searchName = '') {
    this.isLoading.set(true);
    if (searchName) this.currentPage.set(1);
    this.subscriptions.add(
      this.jobTitleService
        .getJobTitleUsers(
          this.jobTitleId,
          this.currentPage(),
          this.currentSize(),
          searchName
        )
        .subscribe({
          next: res => this.handleJobTitleUsers(res),
          complete: () => {
            this.isLoading.set(false);
          },
          error: () => {
            this.isLoading.set(false);
          },
        })
    );
  }

  handleJobTitleUsers(res: any) {
    this.isLoading.set(true);
    this.jobTitleUsers.set(
      res.data.map((user: any) => {
        return {
          label: user.name,
          value: user.id.toString(),
          image: user.image,
          email: user.email,
        };
      })
    );

    this.totalUsersCount.set(res.meta.total);
    this.totalPages.set(res.meta.last_page);
  }

  goBack() {
    history.back();
  }

  handlePagination($event: number) {
    this.currentPage.set($event);
    this.getJobTitleUsers();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
